import { EAppErrorKind, logError, logWarning } from '@/utils/error';
import { matchError, type AppErrorHandlers } from './matchError';

/**
 * 用户输入的正常结果:密码错误(422)、邮箱已注册(409)、验证码错误(403)、参数不合法(400)。
 * 调用点必定给出可见提示,所以留痕即可,不该以 error 级别刷控制台。
 */
const EXPECTED_INPUT_KINDS: ReadonlySet<EAppErrorKind> = new Set([
  EAppErrorKind.BAD_REQUEST,
  EAppErrorKind.UNPROCESSABLE,
  EAppErrorKind.CONFLICT,
  EAppErrorKind.FORBIDDEN
]);

/** 默认策略需要的外部能力,由 useErrorHandler 从 React 上下文里取好后注入 */
export interface IErrorStrategyDeps {
  /** 提示用户,由 useErrorHandler 注入 toast;要用阻塞式弹窗的面板直接覆盖对应 kind 的 handler */
  notify: (message: string) => void;
  /** 会话失效时跳登录 */
  goLogin: () => void;
  /** 已翻译好的网络错误文案 */
  networkMessage: string;
}

/**
 * 请求错误的默认策略,纯函数 —— 逻辑在这里,useErrorHandler 只负责从 React 上下文接线。
 *
 * 只收敛全站真正一致的行为:网络提示、401 跳登录、主动中断静默、错误日志。
 * 其余分支(尤其是各不相同的兜底文案)仍由调用点通过 handlers 决定;
 * handlers 在 spread 中位于后面,调用点写同名 kind 即可覆盖这里的默认实现。
 */
export function runErrorStrategy(error: unknown, handlers: AppErrorHandlers, deps: IErrorStrategyDeps) {
  const appError = matchError(error, {
    [EAppErrorKind.NETWORK]: () => deps.notify(deps.networkMessage),
    // 取不到 reCAPTCHA 时用户无从自救,只能稍后再试,与网络失败的可行动作一致,因此共用该文案。
    // 注意这条文案是「网络异常,请稍后重试」而非「请检查您的网络连接」—— 不对是谁的网络出问题下断言。
    [EAppErrorKind.RECAPTCHA_UNAVAILABLE]: () => deps.notify(deps.networkMessage),
    [EAppErrorKind.UNAUTHORIZED]: () => deps.goLogin(),
    // 主动中断不是失败,不打扰用户
    [EAppErrorKind.CANCELED]: () => {},
    ...handlers
  });

  // 记 appError 而非 cause:它自带 cause,同时保留 kind / httpStatus / bizCode / serverMessage
  if (appError.kind === EAppErrorKind.CANCELED) return appError;
  if (EXPECTED_INPUT_KINDS.has(appError.kind)) logWarning(appError);
  else logError(appError);

  return appError;
}
